/**
 * User Overview API Service
 * 
 * New email-based API service that provides a unified endpoint for fetching
 * user's folders and data objects in a single call.
 * Based on the React Frontend Integration Guide for backend API changes.
 */

import { httpClient, unwrapApiResult } from './authenticatedHttpClient';
import { apiPath } from '../config/env';
import { encodeEmailForUrl } from '../utils/emailUtils';

// Response interfaces matching the backend
export interface FolderSummary {
    id: string;
    name: string;
    data_object_count: number;
    created_at: string;
}

export interface DataObjectSummary {
    id: string;
    original_filename: string; // Backend sends original_filename, not name
    folder_id?: string;
    status: string;
    created_at: string;
}

export interface UserOverviewResponse {
    user_email: string;
    folders: FolderSummary[];
    data_objects: DataObjectSummary[];
    total_folders: number;
    total_data_objects: number;
    is_first_login?: boolean;
}

export interface UserOverviewParams {
    include_empty_folders?: boolean;
    folder_id?: string;
    limit_folders?: number;
    limit_data_objects?: number;
    include_inactive?: boolean;
    include_deleted?: boolean;
    status_filter?: string;
    include_all_statuses?: boolean;
}

/**
 * Get user overview (folders + data objects) for the specified email
 */
export async function getUserOverview(
    userEmail: string,
    params?: UserOverviewParams
): Promise<UserOverviewResponse> {
    const encodedEmail = encodeEmailForUrl(userEmail);
    const searchParams = new URLSearchParams();

    // Add user_email as query parameter (NEW format)
    searchParams.append('user_email', encodedEmail);

    if (params?.include_empty_folders !== undefined) {
        searchParams.append('include_empty_folders', params.include_empty_folders.toString());
    }
    if (params?.folder_id) {
        searchParams.append('folder_id', params.folder_id);
    }
    if (params?.limit_folders) {
        searchParams.append('limit_folders', params.limit_folders.toString());
    }
    if (params?.limit_data_objects) {
        searchParams.append('limit_data_objects', params.limit_data_objects.toString());
    }
    if (params?.include_inactive !== undefined) {
        searchParams.append('include_inactive', params.include_inactive.toString());
    }
    if (params?.include_deleted !== undefined) {
        searchParams.append('include_deleted', params.include_deleted.toString());
    }
    if (params?.status_filter) {
        searchParams.append('status_filter', params.status_filter);
    }
    if (params?.include_all_statuses !== undefined) {
        searchParams.append('include_all_statuses', params.include_all_statuses.toString());
    }

    const queryString = searchParams.toString();
    const url = apiPath(`user-overview/?${queryString}`);

    const result = await httpClient.get<UserOverviewResponse>(url);
    return unwrapApiResult(result);
}

/**
 * Get user overview using the unified endpoint only
 * No fallback - container app must have the unified endpoint available
 */
export async function getUserOverviewWithFallback(
    userEmail: string,
    params?: UserOverviewParams
): Promise<UserOverviewResponse> {
    return getUserOverview(userEmail, params);
}